import React, { useState, useEffect } from 'react';
import {
    X,
    Save,
    Trash2,
    Calendar,
    Tag,
    User,
    CheckSquare,
    Square,
    FileText,
    Flag,
    Plus,
    Move
} from 'lucide-react';

const EditTaskModal = ({ show, task, columns, onClose, onSubmit, onDelete }) => {
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        priority: 'medium',
        dueDate: '',
        startDate: '',
        tags: [],
        checklist: [],
        assignee: null,
        columnId: ''
    });
    const [newTag, setNewTag] = useState('');
    const [newChecklistItem, setNewChecklistItem] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (task) {
            setFormData({
                title: task.title || '',
                description: task.description || '',
                priority: task.priority || 'medium',
                dueDate: task.dueDate ? task.dueDate.split('T')[0] : '',
                startDate: task.startDate ? task.startDate.split('T')[0] : '',
                tags: task.tags || [],
                checklist: task.checklist || [],
                assignee: task.assignee || null,
                columnId: task.columnId || ''
            });
        }
    }, [task]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handleAddTag = () => {
        if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
            setFormData(prev => ({
                ...prev,
                tags: [...prev.tags, newTag.trim()]
            }));
            setNewTag('');
        }
    };

    const handleRemoveTag = (tagToRemove) => {
        setFormData(prev => ({
            ...prev,
            tags: prev.tags.filter(tag => tag !== tagToRemove)
        }));
    };

    const handleAddChecklistItem = () => {
        if (newChecklistItem.trim()) {
            setFormData(prev => ({
                ...prev,
                checklist: [...prev.checklist, {
                    id: Date.now().toString(),
                    text: newChecklistItem.trim(),
                    completed: false
                }]
            }));
            setNewChecklistItem('');
        }
    };

    const handleRemoveChecklistItem = (itemId) => {
        setFormData(prev => ({
            ...prev,
            checklist: prev.checklist.filter(item => item.id !== itemId)
        }));
    };

    const handleToggleChecklistItem = (itemId) => {
        setFormData(prev => ({
            ...prev,
            checklist: prev.checklist.map(item =>
                item.id === itemId ? { ...item, completed: !item.completed } : item
            )
        }));
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.title.trim()) {
            newErrors.title = 'Task title is required';
        }

        if (formData.startDate && formData.dueDate) {
            const startDate = new Date(formData.startDate);
            const dueDate = new Date(formData.dueDate);

            if (startDate >= dueDate) {
                newErrors.dueDate = 'Due date must be after start date';
            }
        }

        return newErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const validationErrors = validateForm();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        setIsSubmitting(true);

        try {
            const updatedTask = {
                ...task,
                ...formData
            };

            await onSubmit(updatedTask);
            setErrors({});
        } catch (error) {
            setErrors({ submit: error.message || 'An error occurred' });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = () => {
        if (window.confirm('Are you sure you want to delete this task? This action cannot be undone.')) {
            onDelete();
            onClose();
        }
    };

    const handleClose = () => {
        setErrors({});
        setNewTag('');
        setNewChecklistItem('');
        onClose();
    };

    if (!show || !task) return null;

    return (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && handleClose()}>
            <div className="modal-container large">
                <div className="modal-header">
                    <h2 className="modal-title">
                        <FileText style={{ width: '20px', height: '20px' }} />
                        Edit Task
                    </h2>
                    <button
                        className="modal-close"
                        onClick={handleClose}
                        disabled={isSubmitting}
                    >
                        <X style={{ width: '20px', height: '20px' }} />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="modal-form">
                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="title" className="form-label">
                                <FileText style={{ width: '16px', height: '16px' }} />
                                Task Title *
                            </label>
                            <input
                                id="title"
                                type="text"
                                name="title"
                                value={formData.title}
                                onChange={handleInputChange}
                                className={`form-input ${errors.title ? 'error' : ''}`}
                                placeholder="Enter task title..."
                                disabled={isSubmitting}
                            />
                            {errors.title && <span className="error-text">{errors.title}</span>}
                        </div>

                        <div className="form-group">
                            <label htmlFor="priority" className="form-label">
                                <Flag style={{ width: '16px', height: '16px' }} />
                                Priority
                            </label>
                            <select
                                id="priority"
                                name="priority"
                                value={formData.priority}
                                onChange={handleInputChange}
                                className="form-select"
                                disabled={isSubmitting}
                            >
                                <option value="low">Low</option>
                                <option value="medium">Medium</option>
                                <option value="high">High</option>
                            </select>
                        </div>
                    </div>

                    <div className="form-group">
                        <label htmlFor="columnId" className="form-label">
                            <Move style={{ width: '16px', height: '16px' }} />
                            Column
                        </label>
                        <select
                            id="columnId"
                            name="columnId"
                            value={formData.columnId}
                            onChange={handleInputChange}
                            className="form-select"
                            disabled={isSubmitting}
                        >
                            {columns.map(column => (
                                <option key={column._id} value={column._id}>
                                    {column.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="form-group">
                        <label htmlFor="description" className="form-label">
                            <FileText style={{ width: '16px', height: '16px' }} />
                            Description
                        </label>
                        <textarea
                            id="description"
                            name="description"
                            value={formData.description}
                            onChange={handleInputChange}
                            className="form-textarea"
                            placeholder="Describe the task..."
                            rows="4"
                            disabled={isSubmitting}
                        />
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="startDate" className="form-label">
                                <Calendar style={{ width: '16px', height: '16px' }} />
                                Start Date
                            </label>
                            <input
                                id="startDate"
                                type="date"
                                name="startDate"
                                value={formData.startDate}
                                onChange={handleInputChange}
                                className="form-input"
                                disabled={isSubmitting}
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="dueDate" className="form-label">
                                <Calendar style={{ width: '16px', height: '16px' }} />
                                Due Date
                            </label>
                            <input
                                id="dueDate"
                                type="date"
                                name="dueDate"
                                value={formData.dueDate}
                                onChange={handleInputChange}
                                className={`form-input ${errors.dueDate ? 'error' : ''}`}
                                disabled={isSubmitting}
                            />
                            {errors.dueDate && <span className="error-text">{errors.dueDate}</span>}
                        </div>
                    </div>

                    <div className="form-group">
                        <label className="form-label">
                            <Tag style={{ width: '16px', height: '16px' }} />
                            Tags
                        </label>
                        <div className="tags-input-container">
                            <input
                                type="text"
                                value={newTag}
                                onChange={(e) => setNewTag(e.target.value)}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleAddTag();
                                    }
                                }}
                                className="form-input"
                                placeholder="Add a tag..."
                                disabled={isSubmitting}
                            />
                            <button
                                type="button"
                                onClick={handleAddTag}
                                className="add-tag-button"
                                disabled={!newTag.trim() || isSubmitting}
                            >
                                <Plus style={{ width: '16px', height: '16px' }} />
                            </button>
                        </div>

                        {formData.tags.length > 0 && (
                            <div className="tags-display">
                                {formData.tags.map((tag, index) => (
                                    <span key={index} className="tag-item">
                                        {tag}
                                        <button
                                            type="button"
                                            onClick={() => handleRemoveTag(tag)}
                                            className="remove-tag-button"
                                            disabled={isSubmitting}
                                        >
                                            <Trash2 style={{ width: '12px', height: '12px' }} />
                                        </button>
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="form-group">
                        <label className="form-label">
                            <CheckSquare style={{ width: '16px', height: '16px' }} />
                            Checklist
                        </label>
                        <div className="checklist-input-container">
                            <input
                                type="text"
                                value={newChecklistItem}
                                onChange={(e) => setNewChecklistItem(e.target.value)}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleAddChecklistItem();
                                    }
                                }}
                                className="form-input"
                                placeholder="Add checklist item..."
                                disabled={isSubmitting}
                            />
                            <button
                                type="button"
                                onClick={handleAddChecklistItem}
                                className="add-tag-button"
                                disabled={!newChecklistItem.trim() || isSubmitting}
                            >
                                <Plus style={{ width: '16px', height: '16px' }} />
                            </button>
                        </div>

                        {formData.checklist.length > 0 && (
                            <div className="checklist-display">
                                {formData.checklist.map((item) => (
                                    <div key={item.id} className="checklist-item">
                                        <button
                                            type="button"
                                            onClick={() => handleToggleChecklistItem(item.id)}
                                            className="checkbox-button"
                                            disabled={isSubmitting}
                                        >
                                            {item.completed ?
                                                <CheckSquare style={{ width: '16px', height: '16px' }} /> :
                                                <Square style={{ width: '16px', height: '16px' }} />
                                            }
                                        </button>
                                        <span className={item.completed ? 'completed' : ''}>{item.text}</span>
                                        <button
                                            type="button"
                                            onClick={() => handleRemoveChecklistItem(item.id)}
                                            className="remove-item-button"
                                            disabled={isSubmitting}
                                        >
                                            <Trash2 style={{ width: '14px', height: '14px' }} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {errors.submit && (
                        <div className="error-message">
                            {errors.submit}
                        </div>
                    )}

                    <div className="modal-footer">
                        <div className="footer-left">
                            <button
                                type="button"
                                onClick={handleDelete}
                                className="button danger"
                                disabled={isSubmitting}
                            >
                                <Trash2 style={{ width: '16px', height: '16px' }} />
                                Delete Task
                            </button>
                        </div>
                        <div className="footer-right">
                            <button
                                type="button"
                                onClick={handleClose}
                                className="button secondary"
                                disabled={isSubmitting}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="button primary"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? (
                                    <>
                                        <div className="spinner"></div>
                                        Updating...
                                    </>
                                ) : (
                                    <>
                                        <Save style={{ width: '16px', height: '16px' }} />
                                        Update Task
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditTaskModal;